import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Box,
  Typography,
  MenuItem,
  Stack,
  Alert,
  Chip,
} from '@mui/material';
import { WhatsappTemplate } from '../../services/whatsappService';
import { SPOP } from '../../types';
import {
  TEMPLATE_TYPE_OPTIONS,
  TemplateFormState,
  getTemplateVariables,
} from '../../constants/whatsappTemplateConstants';

interface Props {
  open: boolean;
  editMode: boolean;
  template: TemplateFormState;
  spops: SPOP[];
  operatorSpopId?: string;
  loading: boolean;
  onChange: (template: TemplateFormState) => void;
  onClose: () => void;
  onSave: () => void;
}

const CUSTOMER_STATUS_OPTIONS = [
  { value: '', label: 'Default — semua status' },
  { value: 'active', label: 'Aktif' },
  { value: 'inactive', label: 'Tidak aktif' },
  { value: 'suspended', label: 'Suspend' },
  { value: 'free', label: 'Free' },
  { value: 'NonInternet', label: 'Non Internet' },
];

const WhatsappTemplateDialog: React.FC<Props> = ({
  open,
  editMode,
  template,
  spops,
  operatorSpopId,
  loading,
  onChange,
  onClose,
  onSave,
}) => {
  const variables = getTemplateVariables(template.type);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{editMode ? 'Edit template' : 'Tambah template'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <TextField
            fullWidth
            label="Nama template"
            value={template.name}
            onChange={(e) => onChange({ ...template, name: e.target.value })}
            required
            size="small"
          />

          <TextField
            select
            fullWidth
            label="Tipe template"
            value={template.type}
            onChange={(e) =>
              onChange({ ...template, type: e.target.value as WhatsappTemplate['type'] })
            }
            required
            size="small"
          >
            {TEMPLATE_TYPE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              fullWidth
              label="Status pelanggan"
              value={template.status || ''}
              onChange={(e) =>
                onChange({ ...template, status: e.target.value || undefined })
              }
              size="small"
              helperText="Kosongkan untuk template default"
            >
              {CUSTOMER_STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value || 'default'} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>

            {operatorSpopId ? (
              <TextField
                fullWidth
                label="SPOP target"
                value={
                  spops.find((s) => s.id === operatorSpopId)?.name ||
                  operatorSpopId
                }
                size="small"
                disabled
                helperText="Template otomatis untuk SPOP Anda"
              />
            ) : (
              <TextField
                select
                fullWidth
                label="SPOP target"
                value={template.spop_id || ''}
                onChange={(e) =>
                  onChange({
                    ...template,
                    spop_id: e.target.value ? e.target.value : undefined,
                  })
                }
                size="small"
                helperText="Kosongkan untuk semua SPOP"
              >
                <MenuItem value="">Default — semua SPOP</MenuItem>
                {spops.map((spop) => (
                  <MenuItem key={spop.id} value={spop.id}>
                    {spop.name} ({spop.areaCode})
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>

          <Alert severity="info" variant="outlined">
            {operatorSpopId
              ? 'Template SPOP Anda akan diprioritaskan untuk pelanggan di SPOP yang sama.'
              : 'Prioritas pemilihan: exact match (tipe + status + SPOP) → status → SPOP → default.'}
          </Alert>

          <TextField
            fullWidth
            multiline
            rows={6}
            label="Isi pesan"
            value={template.template}
            onChange={(e) => onChange({ ...template, template: e.target.value })}
            required
            size="small"
            placeholder="Halo {customerName}, tagihan Anda sebesar {amount} jatuh tempo {dueDate}. Bayar: {link}"
          />

          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom>
              Variabel untuk {TEMPLATE_TYPE_OPTIONS.find((o) => o.value === template.type)?.label}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {variables.map((v) => (
                <Chip
                  key={v.key}
                  label={v.key}
                  size="small"
                  variant="outlined"
                  title={v.desc}
                  onClick={() =>
                    onChange({ ...template, template: `${template.template}${v.key}` })
                  }
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Klik variabel untuk menyisipkan ke pesan. {'{link}'} otomatis menyesuaikan gateway aktif.
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={template.isActive ?? true}
                onChange={(e) => onChange({ ...template, isActive: e.target.checked })}
              />
            }
            label="Template aktif"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Batal
        </Button>
        <Button onClick={onSave} variant="contained" disabled={loading}>
          Simpan
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WhatsappTemplateDialog;
